import { useMemo } from 'react';
import {
  getTranslations,
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  type Locale,
  type TranslationKey,
  type TranslationDictionary,
} from '@flymind/shared';
import { useAuth } from './useAuth';

function getNestedValue(obj: unknown, path: string): string {
  const keys = path.split('.');
  let current: unknown = obj;
  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return path;
    }
    current = (current as Record<string, unknown>)[key];
  }
  return typeof current === 'string' ? current : path;
}

export function useTranslation() {
  let userLocale: string | undefined;
  try {
    const auth = useAuth();
    userLocale = auth.user?.locale;
  } catch {
    // useAuth may throw if outside AuthProvider (e.g. in public layouts).
    // Fall back to browser locale.
  }

  const locale: Locale = useMemo(() => {
    // 1. User profile locale (authenticated)
    if (userLocale && (SUPPORTED_LOCALES as readonly string[]).includes(userLocale)) {
      return userLocale as Locale;
    }
    // 2. localStorage override (public pages, set by language toggle)
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('flymind_locale') : null;
    if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
      return stored as Locale;
    }
    // 3. Default: Italian (site is Italian-first)
    return DEFAULT_LOCALE;
  }, [userLocale]);

  const translations: TranslationDictionary = useMemo(
    () => getTranslations(locale),
    [locale],
  );

  const t = useMemo(() => {
    return (key: TranslationKey): string => getNestedValue(translations, key);
  }, [translations]);

  return { t, locale };
}
